import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, toStorageError } from '../errors'
import { notifyWritten } from '../invalidation'
import { META_KEY, STORE } from '../schema'

/**
 * Operational state (D-046).
 *
 * `lastBackupAt` lives here rather than in settings because it is written by
 * the backup service, not chosen by the user, and must survive a settings
 * reset. This is its single canonical authority.
 */
export async function getMeta<T>(
  db: SolaDatabase,
  key: string,
): Promise<Result<T | undefined, StorageError>> {
  try {
    const row = await db.get(STORE.meta, key)
    return ok(row?.value as T | undefined)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function setMeta(
  db: SolaDatabase,
  key: string,
  value: unknown,
): Promise<Result<void, StorageError>> {
  try {
    await db.put(STORE.meta, { key, value })
    notifyWritten(STORE.meta)
    return ok(undefined)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function getLastBackupAt(
  db: SolaDatabase,
): Promise<Result<string | undefined, StorageError>> {
  return getMeta<string>(db, META_KEY.lastBackupAt)
}

/** Called only on a successfully completed full backup (D-022). */
export async function setLastBackupAt(
  db: SolaDatabase,
  isoTimestamp: string,
): Promise<Result<void, StorageError>> {
  return setMeta(db, META_KEY.lastBackupAt, isoTimestamp)
}
